import React, {useEffect, useState} from 'react';
import {addBookmark, removeBookmark} from '../endpoints/api';
import {ToastMessage} from '../components/toast';
import {Article} from '../components/Articles';
import {Link} from '@chakra-ui/react';
import {Place, Post, Event} from '../types/article';
import {Offer} from '../types/article';
import {useMutation, useQueryClient} from '@tanstack/react-query';
import useAuth from './useAuth';
import {map, set} from 'lodash';

/* 
  Invalidating this query causes this to return different places from the backend
  because places from the previous call already being seen. Iterate through query data
  update this place's bookmark instead of invalidating entire query.
*/
const updateInfiniteQueryData = (queryClient, queryKey, articleId, remove) => {
  const previousData = queryClient.getQueriesData([queryKey]);

  if (!previousData || previousData.length <= 0) return;

  map(previousData, (prevData, index) => {
    set(
      previousData,
      `[${index}][1].pages`,
      map(previousData[index][1]?.pages, (page) => {
        return {
          ...page,
          data: {
            ...page.data,
            data: map(page.data.data, (dData) => {
              return {
                ...dData,
                is_bookmarked: dData?.id == articleId ? !remove : dData?.is_bookmarked,
              };
            }),
          },
        };
      })
    );
  });

  // queryClient.setQueryData([queryKey], previousData);
};

// Optimistically flip is_bookmarked on matching cards in the three discover sections so tapping
// feels instant. `bookmarked_upcoming` and `meta.bookmarked_upcoming_count` are NOT touched here
// — the caller invalidates the feed so the server response is the single source of truth for
// both the section contents and the banner count.
const updateBookmarkAheadFeedCache = (queryClient, articleId, remove = false) => {
  queryClient.setQueryData(['bookmark-ahead-feed'], (oldData: any) => {
    if (!oldData?.data?.data) return oldData;
    const updateArticles = (articles: any[]) =>
      articles?.map((a: any) =>
        a.id?.toString() === articleId?.toString() ? {...a, is_bookmarked: !remove} : a,
      ) || [];
    return {
      ...oldData,
      data: {
        ...oldData.data,
        data: {
          ...oldData.data.data,
          hyper_local: updateArticles(oldData.data.data.hyper_local),
          system_popular: updateArticles(oldData.data.data.system_popular),
          general_upcoming: updateArticles(oldData.data.data.general_upcoming),
        },
      },
    };
  });
};

// All the react-query queries related to bookmarks have to be invalidated when a bookmark is changed
const invalidateQueries = (queryClient, type, articleId, remove = false) => {
  if (type == 'place') {
    queryClient.removeQueries(['placesByType'], {
      predicate: (query) => query.queryKey[1].typeIds.length <= 0,
    });
    queryClient.invalidateQueries([`placesByType`]);
    queryClient.invalidateQueries(['placeInterestedUsers', String(articleId)]);

    // queryClient.invalidateQueries(['infinite-placesByType']);
    updateInfiniteQueryData(queryClient, 'infinite-placesByType', articleId, remove);
  }
  if (type == 'article') {
    queryClient.invalidateQueries([`post`]);
    queryClient.invalidateQueries(['posts']);
    //queryClient.invalidateQueries([`infinite-posts`]);
    updateInfiniteQueryData(queryClient, 'infinite-posts', articleId, remove);
  } else {
    queryClient.invalidateQueries([`${type}`, {id: articleId}]);
    queryClient.invalidateQueries([`${type}s`]);
    //queryClient.invalidateQueries([`infinite-${type}s`]);
    updateInfiniteQueryData(queryClient, `infinite-${type}s`, articleId, remove);
  }

  // Bookmarking an event/meet-up auto-sets RSVP to Interested on the backend,
  // so invalidate RSVP-related queries to reflect the updated status
  if (type === 'event' || type === 'meet-up') {
    queryClient.invalidateQueries(['event', articleId]);
    queryClient.invalidateQueries(['meetup', articleId]);
    queryClient.invalidateQueries(['RSVPStats', articleId]);
    queryClient.invalidateQueries(['eventInterestedGoingUsers', articleId]);
  }

  updateInfiniteQueryData(queryClient, 'infinite-global-search', articleId, remove);
  queryClient.invalidateQueries([`infinite-bookmarks`]);
  //updateInfiniteQueryData(queryClient, `infinite-bookmarks`, articleId, remove);
  queryClient.invalidateQueries([`current-home-context-content`]);
  
  // Flip is_bookmarked in the bookmark-ahead-feed cache immediately so the card re-styles,
  // then invalidate so the banner count (meta.bookmarked_upcoming_count) reconciles to the
  // server value on the next refetch.
  updateBookmarkAheadFeedCache(queryClient, articleId, remove);
  queryClient.invalidateQueries(['bookmark-ahead-feed']);

  // Invalidate user plans caches (neighbor profile shared plans)
  queryClient.invalidateQueries(['userPlans']);
};

const bookmarkUrls = {
  place: {
    add: (placeId) => `places/${placeId}/bookmark`,
    remove: (placeId) => `places/${placeId}/remove-bookmark`,
  },
  event: {
    add: (eventId) => `events/${eventId}/add`,
    remove: (eventId) => `events/${eventId}/remove`,
  },
  offer: {
    add: (offerId) => `offers/${offerId}/bookmark`,
    remove: (offerId) => `offers/${offerId}/remove-bookmark`,
  },
  'meet-up': {
    add: (eventId) => `events/${eventId}/add`,
    remove: (eventId) => `events/${eventId}/remove`,
  },
};

export type ArticleType = Article | Post | Event | Place | Offer;

export const useAddBookmark = () => {
  const queryClient = useQueryClient();
  return useMutation(
    ({ articleId, type = 'article' }: { articleId: number; type: string }) => {
      return (
        addBookmark({articleId: articleId, customUrl: bookmarkUrls[type]?.add})
      );
    },
    {
      onSettled: (newComment, error, variables, context) => {
        invalidateQueries(queryClient, variables.type, variables.articleId);
      },
    }
  );
};

export const useRemoveBookmark = () => {
  const queryClient = useQueryClient();
  return useMutation(
    ({articleId, type = 'article'}: {articleId: number; type: string}) => {
      return removeBookmark({articleId, customUrl: bookmarkUrls[type]?.remove});
    },
    {
      onSettled: (_data, _error, variables) => {
        invalidateQueries(queryClient, variables.type, variables.articleId, true);
      },
    },
  );
};

const useBookmark = (
  article: ArticleType,
  type: string = 'article',
  showAction: boolean = false
) => {
  const [isBookmarked, setIsBookmarked] = useState(article?.is_bookmarked);
  const [isChanging, setIsChanging] = useState(false);
  const queryClient = useQueryClient();
  const {user} = useAuth();

  useEffect(() => {
    // Only sync from props when NOT in the middle of an optimistic update
    if (!isChanging && isBookmarked !== article?.is_bookmarked) {
      setIsBookmarked(article?.is_bookmarked);
    }
  }, [article, article?.is_bookmarked]);

  const handleOnBookmarkClick = (e): any => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    if (!article || isChanging) return;
    const changeToState = !isBookmarked;
    setIsChanging(true);
    setIsBookmarked(changeToState);

    // Optimistically update event/meetup query data so the useEffect sync doesn't reset state
    if (type === 'event' || type === 'meet-up') {
      const queryKey = type === 'meet-up' ? 'meetup' : 'event';
      const previousData: any = queryClient.getQueryData([queryKey, article?.id]);
      if (previousData) {
        queryClient.setQueryData([queryKey, article?.id], {
          ...previousData,
          data: {
            ...previousData.data,
            is_bookmarked: changeToState,
          },
        });
      }
    }

    if (changeToState) {
      // Optimistically add user to interested list for places
      if (type === 'place' && user) {
        const previousInterestedUsers: any = queryClient.getQueryData(['placeInterestedUsers', String(article?.id)]);
        if (previousInterestedUsers) {
          queryClient.setQueryData(['placeInterestedUsers', String(article?.id)], {
            ...previousInterestedUsers,
            pages: previousInterestedUsers.pages.map((page, index) => {
              if (index === 0) {
                // Add user to first page
                return {
                  ...page,
                  data: {
                    ...page.data,
                    total: page.data.total + 1,
                    data: [
                      {
                        user_id: user.id,
                        name: user.name,
                        handle: user.handle,
                        profile_photo_url: user.profilePhotoUrl,
                        created_at: new Date().toISOString(),
                      },
                      ...page.data.data,
                    ],
                  },
                };
              }
              return {
                ...page,
                data: {
                  ...page.data,
                  total: page.data.total + 1,
                },
              };
            }),
          });
        }
      }

      return addBookmark({articleId: article?.id, customUrl: bookmarkUrls[type]?.add}).then(() => {
        setIsChanging(false);
        invalidateQueries(queryClient, type, article?.id);
        return ToastMessage({
          status: 'success',
          showStatusIcon: true,
          text: 'Saved in your Bookmarks!',
          ...(showAction && {
            action: <Link href={'/my-stuff?tab=bookmarks'}>View</Link>,
          }),
        });
      }).catch(() => {
        // Revert optimistic update on failure
        setIsBookmarked(false);
        setIsChanging(false);
        if (type === 'event' || type === 'meet-up') {
          const queryKey = type === 'meet-up' ? 'meetup' : 'event';
          const cachedData: any = queryClient.getQueryData([queryKey, article?.id]);
          if (cachedData) {
            queryClient.setQueryData([queryKey, article?.id], {
              ...cachedData,
              data: {...cachedData.data, is_bookmarked: false},
            });
          }
        }
        return ToastMessage({
          status: 'error',
          text: 'Failed to bookmark. Please try again.',
        });
      });
    } else {
      // Optimistically remove user from interested list for places
      if (type === 'place' && user) {
        const previousInterestedUsers: any = queryClient.getQueryData(['placeInterestedUsers', String(article?.id)]);
        if (previousInterestedUsers) {
          queryClient.setQueryData(['placeInterestedUsers', String(article?.id)], {
            ...previousInterestedUsers,
            pages: previousInterestedUsers.pages.map((page) => ({
              ...page,
              data: {
                ...page.data,
                total: Math.max(0, page.data.total - 1),
                data: page.data.data.filter((u) => u.user_id !== user.id),
              },
            })),
          });
        }
      }

      return removeBookmark({articleId: article?.id, customUrl: bookmarkUrls[type]?.remove}).then(
        () => {
          setIsChanging(false);
          invalidateQueries(queryClient, type, article?.id, true);
          return ToastMessage({
            status: 'info',
            text: 'Removed from your Bookmarks.',
          });
        }
      ).catch(() => {
        // Revert optimistic update on failure
        setIsBookmarked(true);
        setIsChanging(false);
        if (type === 'event' || type === 'meet-up') {
          const queryKey = type === 'meet-up' ? 'meetup' : 'event';
          const cachedData: any = queryClient.getQueryData([queryKey, article?.id]);
          if (cachedData) {
            queryClient.setQueryData([queryKey, article?.id], {
              ...cachedData,
              data: {...cachedData.data, is_bookmarked: true},
            });
          }
        }
        return ToastMessage({
          status: 'error',
          text: 'Failed to remove bookmark. Please try again.',
        });
      });
    }
  };

  return {
    isBookmarked,
    handleOnBookmarkClick,
  };
};

export default useBookmark;
